/**
 * 휴가 대상 3-state 편집 — 보건휴가 · 수면OFF · 임산부.
 *
 * 세 값 모두 null(미설정) / true(포함) / false(제외) 다.
 *   미설정 : 자동판정에 맡긴다. 보건휴가는 여성·N전담 아님·고정근무 아님이면 대상.
 *   포함   : 자동판정에서 빠지는 사람도 대상으로 넣는다(고정근무자 등).
 *   제외   : 자동판정에 들어와도 뺀다(임산부 등).
 *
 * ★ 서버는 **보내지 않은 값과 명시적 null 을 가른다.** 안 보내면 그대로 두고,
 *   null 을 보내야 미설정으로 되돌아간다. 그래서 바뀐 항목 하나만 보낸다.
 * ★ 적용 시점은 사이드프로필 상단의 월 셀렉터(useNurseAsOfMonthStore)를 따른다.
 *   그 달 1일부터 발효하고, 이후 달은 다시 바꾸기 전까지 이 값을 물려받는다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import httpClient from "@/config/axios";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

type TriState = "unset" | "include" | "exclude";

type LeaveFlagRow = {
  nurse_id: string;
  name?: string | null;
  health_leave_eligible: boolean | null;
  sleep_off_eligible: boolean | null;
  pregnant: boolean | null;
};

const FIELDS = [
  {
    key: "health_leave_eligible" as const,
    label: "보건휴가 대상",
    note: "미설정이면 여성·나이트 전담 아님·고정근무 아님을 자동 판정합니다.",
  },
  {
    key: "sleep_off_eligible" as const,
    label: "수면OFF 대상",
    note: "미설정이면 나이트를 서는 전원이 대상입니다.",
  },
  {
    key: "pregnant" as const,
    label: "임산부",
    note: "포함으로 두면 보건휴가 자동 부여에서 제외합니다.",
  },
];

const OPTIONS: ReadonlyArray<{ value: TriState; label: string }> = [
  { value: "unset", label: "미설정" },
  { value: "include", label: "포함" },
  { value: "exclude", label: "제외" },
];

function toTriState(v: boolean | null | undefined): TriState {
  if (v === true) return "include";
  if (v === false) return "exclude";
  return "unset";
}

function toValue(s: TriState): boolean | null {
  if (s === "include") return true;
  if (s === "exclude") return false;
  return null;
}

export default function LeaveEligibilityFields({
  nurseId,
  groupId,
  year,
  month,
  disabled = false,
}: {
  readonly nurseId: string;
  readonly groupId: string;
  readonly year: number;
  readonly month: number;
  readonly disabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["LEAVE_FLAGS", groupId, year, month] as const;

  const { data, isLoading, isError } = useQuery({
    queryKey,
    enabled: Boolean(groupId && nurseId),
    queryFn: async () => {
      // httpClient 는 인터셉터에서 response.data 를 벗겨 본문을 직접 돌려준다.
      const res = await httpClient.get<{ rows: LeaveFlagRow[] }>(
        `/nurse-period/leave-flags`,
        { params: { year, month, group_id: groupId } }
      );
      return (res as unknown as { rows?: LeaveFlagRow[] })?.rows ?? [];
    },
  });

  const row = data?.find(
    (r: LeaveFlagRow) => String(r.nurse_id) === String(nurseId)
  );

  const saveMutation = useMutation({
    mutationFn: async (patch: Record<string, boolean | null>) => {
      // 그 달 1일부터 발효. 서버는 미지정 시 today 로 떨어지는데, 월 셀렉터와
      // 어긋나면 "지난 달을 보며 고쳤는데 이번 달부터 적용"되는 혼란이 생긴다.
      const validFrom = `${year}-${String(month).padStart(2, "0")}-01`;
      await httpClient.post(`/nurse-period/leave-flags`, {
        nurse_id: nurseId,
        group_id: groupId,
        valid_from: validFrom,
        ...patch,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast.success("휴가 대상 설정이 저장되었습니다.");
    },
    onError: () => {
      toast.error("휴가 대상 설정 저장에 실패했습니다.");
    },
  });

  if (isError) {
    return (
      <div className="rounded-lg border border-[#E4E7EC] bg-[#F8FAFC] px-4 py-3">
        <p className="text-xs text-[#667085]">
          휴가 대상 설정을 불러오지 못했습니다. 스키마가 반영되지 않은 환경일 수 있습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {FIELDS.map((f) => {
        const current = toTriState(row?.[f.key]);
        return (
          <div
            key={f.key}
            className="rounded-lg border border-[#E4E7EC] bg-white px-4 py-3"
          >
            <div className="flex flex-col gap-1">
              <Label className="text-sm font-semibold text-[#344054]">
                {f.label}
              </Label>
              <p className="break-keep text-xs leading-5 text-[#667085]">
                {f.note}
              </p>
            </div>
            <RadioGroup
              className="mt-3 flex flex-row gap-4"
              value={current}
              disabled={disabled || isLoading || saveMutation.isPending}
              onValueChange={(next) => {
                if (next === current) return;
                // 바뀐 항목 하나만 보낸다 — 나머지는 서버가 직전 구간에서 승계한다.
                saveMutation.mutate({ [f.key]: toValue(next as TriState) });
              }}
            >
              {OPTIONS.map((o) => (
                <div key={o.value} className="flex items-center gap-2">
                  <RadioGroupItem
                    id={`${f.key}-${o.value}-${nurseId}`}
                    value={o.value}
                    className="peer shadow-none data-[state=checked]:border-sig data-[state=checked]:border-2 data-[state=checked]:text-sig [&_svg]:fill-current"
                  />
                  <Label
                    htmlFor={`${f.key}-${o.value}-${nurseId}`}
                    className="cursor-pointer text-sm text-[#344054] peer-data-[state=checked]:font-semibold peer-data-[state=checked]:text-sig"
                  >
                    {o.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      })}
    </div>
  );
}
